import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from 'src/environments/environment';
import { ConfigurationSettingsDto } from 'src/core/models/configuration-setting/configuration-settings.dto';
import { CallLanguageCodeDto } from 'src/core/models/call/call-language-code.dto';
import { catchError, map, switchMap } from 'rxjs/operators';
import { UserStateSettingsDto } from 'src/core/models/configuration-setting/user-state-settings.dto';
import { InitialSettingsDto } from 'src/core/models/configuration-setting/initial-settings.dto';
import { CAConfigStateService } from '../config/ca-config-state.service';
import { ConfigStateService } from '@abp/ng.core';
import { CategoryExceptionDto } from 'src/core/models/query/category-exception.dto';
import { ListRequestDto } from 'src/core/models/request/list-request.dto';
import { ListResponseDto } from 'src/core/models/request/list-response.dto';

@Injectable({
  providedIn: 'root',
})
export class ConfigurationSettingsService {
  private apiBase = environment.apis.default.url;

  constructor(
    private http: HttpClient,
    private configStateService: CAConfigStateService,
    private config: ConfigStateService,
  ) {}

  saveSetting(settings: ConfigurationSettingsDto[]): Observable<any> {
    let tenantId = this.config.getDeep(['currentUser', 'tenantId']);
    let isHost = tenantId == null;
    const address = isHost
      ? `${this.apiBase}/api/app/configuration-settings/host-settings/settings`
      : `${this.apiBase}/api/app/configuration-settings/settings`;

    return this.http.post(address, settings).pipe(
      map(
        response => {
          this.configStateService.refreshAppState().subscribe();

          return response;
        },
        catchError(() => {
          return null;
        }),
      ),
    ) as Observable<any>;
  }

  saveInitialSettings(initialSettings: InitialSettingsDto): Observable<any> {
    return this.http
      .post(`${this.apiBase}/api/app/configuration-settings/initial-configuration`, initialSettings)
      .pipe(
        switchMap(response =>
          this.configStateService.refreshAppState().pipe(map(() => response)),
        ),
        catchError(() => {
          return of(null);
        }),
      ) as Observable<any>;
  }

  getLanguageCodeList(
    request: ListRequestDto | null,
  ): Observable<ListResponseDto<CallLanguageCodeDto>> {
    let params = request === null ? null : new HttpParams();

    if (request !== null) {
      params = params.append('filters', JSON.stringify(request.filters));
      params = params.append('sorters', JSON.stringify(request.sorters));
      params = params.append('skipCount', JSON.stringify(request.skipCount));
      params = params.append('maxResultCount', JSON.stringify(request.maxResultCount));
    }
    return this.http.get(`${this.apiBase}/` + CallLanguageCodeDto.apiUrl, {
      params,
    }) as Observable<ListResponseDto<CallLanguageCodeDto>>;
  }

  initializeUserStateSettings(): Observable<any[]> {
    return this.http.get<UserStateSettingsDto[]>(`${this.apiBase}/` + UserStateSettingsDto.apiUrl);
  }

  getAIInsightsDailyLimit(): Observable<number> {
    return this.http.get<number>(
      `${this.apiBase}/api/app/configuration-settings/ai-insights-daily-limit`,
    );
  }

  saveCategoryException(dtos: CategoryExceptionDto[]): Observable<any> {
    const address = CategoryExceptionDto.apiUrl;

    return this.http.post(address, dtos).pipe(
      map(
        response => {
          this.configStateService.refreshAppState().subscribe();

          return response;
        },
        catchError(() => {
          return null;
        }),
      ),
    ) as Observable<any>;
  }

  getCategoryExceptions(): Observable<CategoryExceptionDto[]> {
    return this.http.get(`${this.apiBase}/` + CategoryExceptionDto.apiUrl) as Observable<
      CategoryExceptionDto[]
    >;
  }

  restartNonFcr(): Observable<any> {
    return this.http
      .post(`${this.apiBase}/api/app/configuration-settings/settings/restart-nonfcr`, null)
      .pipe(
        map(
          response => {
            this.configStateService.refreshAppState().subscribe();

            return response;
          },
          catchError(() => {
            return null;
          }),
        ),
      ) as Observable<any>;
  }

  uploadSamlCertificate(file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file, file.name);

    return this.http.post<void>(
      `${this.apiBase}/api/app/configuration-settings/import-saml-certificate`,
      formData,
    );
  }
}
